import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { tenantService, uploadService, usuarioService, Restaurante } from '../services';

interface FormTenant {
  name: string;
  slug: string;
  email: string;
  cnpj: string;
  telefone: string;
  logoUrl: string;
  exibirVitrine: boolean;
}

const VAZIO: FormTenant = { name: '', slug: '', email: '', cnpj: '', telefone: '', logoUrl: '', exibirVitrine: true };

/**
 * 🛠️ Operador da plataforma — gerencia TODOS os restaurantes (tenants).
 * Só o admin GLOBAL (role ADMIN) acessa; cada restaurante é um mundo
 * isolado. O operador cria/edita/ativa/desativa e "entra como visitante"
 * (poder emprestado, sem aparecer na lista de usuários do restaurante).
 */
export default function Plataforma() {
  const { user, empresa, logout, entrarComoVisitante } = useAuth();
  const navigate = useNavigate();
  const [restaurantes, setRestaurantes] = useState<Restaurante[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<Restaurante | null>(null);
  const [form, setForm] = useState<FormTenant>(VAZIO);
  // Logo escolhida mas ainda não enviada (só sobe junto com o salvar —
  // evita arquivo órfão se o dono cancelar ou criar restaurante novo)
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [entrando, setEntrando] = useState<string | null>(null);
  const [erro, setErro] = useState('');
  // Troca de senha da própria conta do operador
  const [modalSenhaAberto, setModalSenhaAberto] = useState(false);
  const [senhaAtual, setSenhaAtual] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  const [erroSenha, setErroSenha] = useState('');
  const [sucessoSenha, setSucessoSenha] = useState('');

  const carregar = async () => {
    try {
      const response = await tenantService.listar();
      setRestaurantes(response.data.data);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao carregar restaurantes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const abrirNovo = () => {
    setEditando(null);
    setForm(VAZIO);
    setLogoBase64(null);
    setErro('');
    setModalAberto(true);
  };

  const abrirEdicao = (r: Restaurante) => {
    setEditando(r);
    setForm({
      name: r.name,
      slug: r.slug,
      email: r.email ?? '',
      cnpj: r.cnpj ?? '',
      telefone: r.telefone ?? '',
      logoUrl: r.logo_url ?? '',
      exibirVitrine: r.exibir_vitrine ?? true,
    });
    setErro('');
    setLogoBase64(null);
    setModalAberto(true);
  };

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault();
    setSalvando(true);
    setErro('');
    try {
      const dados = {
        name: form.name,
        logo_url: form.logoUrl.trim() || '',
        exibir_vitrine: form.exibirVitrine,
        ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
        ...(form.email.trim() ? { email: form.email.trim() } : { email: undefined }),
        ...(form.cnpj.trim() ? { cnpj: form.cnpj.trim() } : { cnpj: undefined }),
        ...(form.telefone.trim() ? { telefone: form.telefone.trim() } : { telefone: undefined }),
      };
      if (editando) {
        const salvo = await tenantService.atualizar(editando.id, dados);
        await salvarLogo(salvo.data.data.id);
      } else {
        const criado = await tenantService.criar(dados);
        await salvarLogo(criado.data.data.id);
      }
      setModalAberto(false);
      await carregar();
    } catch (error: any) {
      setErro(error.response?.data?.message || 'Erro ao salvar restaurante');
    } finally {
      setSalvando(false);
    }
  };

  // Envia a logo escolhida (base64 -> /uploads/...) e guarda a URL no form
  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        // Só guarda o base64: o upload acontece junto com o Salvar (o tenant
        // já existe — mesmo em criação — e a pasta da logo é a correta).
        setLogoBase64(String(reader.result));
        setErro('');
      } catch (error: any) {
        setErro(error.response?.data?.message || 'Erro ao ler a logo');
      }
    };
    reader.readAsDataURL(arquivo);
  };

  /** Sobe a logo escolhida (se houver) e grava a URL no restaurante. */
  const salvarLogo = async (tenantId: string) => {
    if (!logoBase64) return;
    const up = await uploadService.enviarLogo(logoBase64, tenantId);
    const novaUrl = up.data.data.url;
    if (novaUrl !== form.logoUrl) {
      await tenantService.atualizar(tenantId, { logo_url: novaUrl });
    }
    setForm((f) => ({ ...f, logoUrl: novaUrl }));
    setLogoBase64(null);
  };

  /**
   * T137 — ativar/desativar restaurante. Um clique acidental aqui derrubava o
   * acesso de todos os usuários e tirava o cardápio público do ar, então a ação
   * pede confirmação com o nome e o efeito; o selo de status não é mais botão.
   */
  const handleToggleAtivo = async (r: Restaurante) => {
    const desativando = r.is_active;
    const mensagem = desativando
      ? `Desativar "${r.name}"?\n\n` +
        `• Os ${r.usuarios} usuário(s) deste restaurante perdem o acesso agora (verão "restaurante desativado").\n` +
        `• O cardápio público e os pedidos por QR saem do ar.\n` +
        `• O histórico e os dados continuam salvos — dá para reativar depois.`
      : `Reativar "${r.name}"?\n\n` +
        `• Os usuários voltam a conseguir entrar.\n` +
        `• O cardápio público volta ao ar.`;
    if (!confirm(mensagem)) return;

    try {
      await tenantService.atualizar(r.id, {
        is_active: !r.is_active,
        ...(desativando ? { motivo: 'Desativado pelo operador da plataforma' } : {}),
      });
      await carregar();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar restaurante');
    }
  };

  /** Entra no restaurante com poder emprestado (sem criar vínculo). */
  const handleEntrar = async (r: Restaurante) => {
    setEntrando(r.id);
    try {
      await entrarComoVisitante(r.id);
      navigate('/restaurante');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao entrar no restaurante');
      setEntrando(null);
    }
  };

  const handleSair = () => {
    logout();
    navigate('/login');
  };

  const abrirTrocarSenha = () => {
    setSenhaAtual('');
    setSenhaNova('');
    setConfirmarSenha('');
    setErroSenha('');
    setSucessoSenha('');
    setModalSenhaAberto(true);
  };

  const handleTrocarSenha = async (e: React.FormEvent) => {
    e.preventDefault();
    setErroSenha('');
    setSucessoSenha('');

    if (senhaNova.length < 6) {
      setErroSenha('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (senhaNova !== confirmarSenha) {
      setErroSenha('A confirmação não confere com a nova senha');
      return;
    }

    setSalvandoSenha(true);
    try {
      await usuarioService.trocarPropriaSenha(senhaAtual, senhaNova);
      setSucessoSenha('Senha alterada com sucesso!');
      setSenhaAtual('');
      setSenhaNova('');
      setConfirmarSenha('');
    } catch (error: any) {
      setErroSenha(error.response?.data?.message || error.message || 'Erro ao trocar a senha');
    } finally {
      setSalvandoSenha(false);
    }
  };

  const linkCardapio = (slug: string) => `${window.location.origin}/?restaurante=${slug}`;

  const [copiado, setCopiado] = useState<string | null>(null);
  const copiarLink = async (slug: string) => {
    const url = linkCardapio(slug);
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(slug);
      setTimeout(() => setCopiado(null), 2000);
    } catch {
      window.prompt('Copie o link do cardápio:', url);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Topo da área do operador */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-2xl leading-none">🛠️</span>
            <div className="min-w-0">
              <p className="font-bold text-primary-400 leading-tight truncate">Operador da plataforma</p>
              <p className="text-[11px] text-gray-500 leading-tight truncate">
                {user?.name} · gerencia todos os restaurantes
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {empresa && (
              <button
                onClick={() => navigate('/restaurante')}
                className="btn-secondary text-sm"
                title={`Voltar ao painel de ${empresa.nome}`}
              >
                ← {empresa.nome}
              </button>
            )}
            <button onClick={abrirTrocarSenha} className="btn-secondary text-sm">
              🔑 Trocar senha
            </button>
            <button onClick={handleSair} className="btn-secondary text-sm">
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-100">🏪 Restaurantes</h1>
            <p className="text-sm text-gray-400 mt-1">
              Cada restaurante tem cardápio, pedidos, estoque e usuários próprios. Clique em
              “Entrar” para visitar (você entra como visitante, sem virar usuário do restaurante).
            </p>
          </div>
          <button onClick={abrirNovo} className="btn-primary">
            ➕ Novo restaurante
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Carregando...</div>
        ) : restaurantes.length === 0 ? (
          <div className="card text-center py-12 text-gray-400">
            Nenhum restaurante cadastrado ainda.
          </div>
        ) : (
          <>
            {/* Tabela (desktop) */}
            <div className="hidden md:block card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Restaurante</th>
                    <th className="text-left py-3 px-4">Link do cardápio</th>
                    <th className="text-left py-3 px-4">Usuários</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-right py-3 px-4">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {restaurantes.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-gray-700/50">
                      <td className="py-3 px-4">
                        <p className="font-medium">{r.name}</p>
                        <p className="text-xs text-gray-500">
                          {r.email || r.cnpj || r.telefone || 'sem contato'}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <a
                            href={linkCardapio(r.slug)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-primary-400 hover:text-primary-300 break-all"
                          >
                            {linkCardapio(r.slug)}
                          </a>
                          <button
                            onClick={() => copiarLink(r.slug)}
                            className="shrink-0 px-2 py-1 rounded text-xs bg-gray-800 hover:bg-gray-700 text-gray-300"
                            title="Copiar link do cardápio"
                          >
                            {copiado === r.slug ? '✓' : '📋'}
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-400">{r.usuarios}</td>
                      <td className="py-3 px-4">
                        {/* T137: selo é só status — a ação virou botão separado */}
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            r.is_active
                              ? 'bg-green-900/60 text-green-300'
                              : 'bg-red-900/60 text-red-300'
                          }`}
                        >
                          {r.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right space-x-2 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleAtivo(r)}
                          className="btn-secondary text-xs"
                          title={r.is_active ? 'Desativar restaurante' : 'Reativar restaurante'}
                        >
                          {r.is_active ? 'Desativar' : 'Ativar'}
                        </button>
                        <button
                          onClick={() => handleEntrar(r)}
                          disabled={entrando === r.id || !r.is_active}
                          className="btn-primary text-xs"
                          title={r.is_active ? 'Entrar como visitante' : 'Restaurante desativado'}
                        >
                          {entrando === r.id ? 'Entrando...' : 'Entrar'}
                        </button>
                        <button onClick={() => abrirEdicao(r)} className="btn-secondary text-xs">
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards (mobile) */}
            <div className="md:hidden space-y-3">
              {restaurantes.map((r) => (
                <div key={r.id} className="card p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-100 truncate">{r.name}</p>
                      <p className="text-[11px] text-primary-400 break-all">{linkCardapio(r.slug)}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {r.usuarios} usuário(s) · {r.email || r.cnpj || r.telefone || 'sem contato'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`shrink-0 px-2 py-1 rounded text-xs font-medium ${
                          r.is_active
                            ? 'bg-green-900/60 text-green-300'
                            : 'bg-red-900/60 text-red-300'
                        }`}
                      >
                        {r.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleToggleAtivo(r)}
                      className="btn-secondary text-xs flex-1"
                    >
                      {r.is_active ? 'Desativar' : 'Ativar'}
                    </button>
                    <button
                      onClick={() => handleEntrar(r)}
                      disabled={entrando === r.id || !r.is_active}
                      className="btn-primary text-xs flex-1"
                    >
                      {entrando === r.id ? 'Entrando...' : 'Entrar'}
                    </button>
                    <button onClick={() => abrirEdicao(r)} className="btn-secondary text-xs flex-1">
                      Editar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Modal criar/editar restaurante */}
      {modalAberto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="card w-full max-w-2xl flex flex-col max-h-[90vh]">
            <div className="px-5 py-4 border-b border-gray-800 flex justify-between items-center shrink-0">
              <h2 className="text-xl font-bold text-gray-100">
                {editando ? '✏️ Editar restaurante' : '➕ Novo restaurante'}
              </h2>
              <button
                onClick={() => setModalAberto(false)}
                className="text-gray-400 hover:text-gray-200 text-xl leading-none"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSalvar} className="flex flex-col min-h-0">
              <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
                {erro && (
                  <div className="bg-red-950/60 border border-red-800 text-red-300 px-4 py-3 rounded-lg text-sm">
                    {erro}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Nome do restaurante *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-field"
                    placeholder="Ex.: Cantina da Praça"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Endereço do cardápio (slug)
                  </label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    className="input-field"
                    placeholder="Vazio: gerado do nome (ex.: cantina-da-praca)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Entra no link do QR: <code className="text-primary-400">?restaurante=cantina-da-praca</code>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Logo do restaurante
                  </label>
                  <div className="flex items-center gap-3 flex-wrap">
                    {form.logoUrl || logoBase64 ? (
                      <img
                        src={logoBase64 ?? form.logoUrl}
                        alt="Logo do restaurante"
                        className="w-14 h-14 rounded-xl object-cover border border-gray-700"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center text-2xl">
                        🏪
                      </div>
                    )}
                    <label className="btn-secondary text-sm cursor-pointer">
                      {form.logoUrl || logoBase64 ? '🔄 Trocar logo' : '⬆️ Enviar logo'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoChange}
                      />
                    </label>
                    {(form.logoUrl || logoBase64) && (
                      <button
                        type="button"
                        onClick={() => {
                          setForm({ ...form, logoUrl: '' });
                          setLogoBase64(null);
                        }}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        🗑️ Remover
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Aparece no cardápio do cliente e na vitrine da landing.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Email de contato</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="input-field"
                      placeholder="contato@restaurante.com.br"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">CNPJ</label>
                    <input
                      type="text"
                      value={form.cnpj}
                      onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                      className="input-field"
                      placeholder="Somente números"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Telefone</label>
                    <input
                      type="text"
                      value={form.telefone}
                      onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                      className="input-field"
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.exibirVitrine}
                    onChange={(e) => setForm({ ...form, exibirVitrine: e.target.checked })}
                    className="w-4 h-4 accent-primary-500"
                  />
                  <span className="text-sm text-gray-300">
                    Exibir na vitrine da landing
                  </span>
                  <span className="text-xs text-gray-500">
                    (aparece como cliente em “Cardápios ativos agora”)
                  </span>
                </label>
              </div>
              <div className="px-5 py-4 border-t border-gray-800 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setModalAberto(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" disabled={salvando} className="btn-primary">
                  {salvando ? 'Salvando...' : editando ? 'Salvar' : 'Criar restaurante'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: troca de senha do operador da plataforma */}
      {modalSenhaAberto && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="card w-full max-w-sm max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between shrink-0">
              <h2 className="font-bold text-gray-100">🔑 Trocar senha</h2>
              <button
                onClick={() => setModalSenhaAberto(false)}
                className="text-gray-400 hover:text-gray-200 text-xl leading-none"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleTrocarSenha} className="flex flex-col">
              <div className="px-5 py-4 space-y-4 overflow-y-auto">
                <p className="text-xs text-gray-500">
                  Conta: <span className="text-gray-300">{user?.email}</span>
                </p>
                {erroSenha && (
                  <div className="bg-red-950/60 border border-red-800 text-red-300 px-4 py-3 rounded-lg text-sm">
                    {erroSenha}
                  </div>
                )}
                {sucessoSenha && (
                  <div className="bg-green-950/60 border border-green-800 text-green-300 px-4 py-3 rounded-lg text-sm">
                    ✅ {sucessoSenha}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Senha atual</label>
                  <input
                    type="password"
                    required
                    value={senhaAtual}
                    onChange={(e) => setSenhaAtual(e.target.value)}
                    className="input-field"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Nova senha</label>
                  <input
                    type="password"
                    required
                    value={senhaNova}
                    onChange={(e) => setSenhaNova(e.target.value)}
                    className="input-field"
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Confirmar nova senha</label>
                  <input
                    type="password"
                    required
                    value={confirmarSenha}
                    onChange={(e) => setConfirmarSenha(e.target.value)}
                    className="input-field"
                    placeholder="Repita a nova senha"
                  />
                </div>
              </div>
              <div className="px-5 py-4 border-t border-gray-800 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setModalSenhaAberto(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" disabled={salvandoSenha} className="btn-primary">
                  {salvandoSenha ? 'Salvando...' : 'Trocar senha'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

